/**
 * Continuous monitoring SLA timers, governed personalization defaults,
 * and review-session evidence bundles.
 */

import {
  CEEG_SLA_SCHEMA_VERSION,
  computeCeegSlaTimers,
  type CeegSlaTimers,
} from "@shared/utils/ceegSlaTimers";

export { CEEG_SLA_SCHEMA_VERSION, computeCeegSlaTimers, type CeegSlaTimers };

export const PERSONALIZATION_SCHEMA_VERSION = "governed-personalization-v1.0.0";
export const EVIDENCE_BUNDLE_SCHEMA_VERSION = "review-evidence-bundle-v1.0.0";

export type PersonalizationScope = "org" | "site" | "role" | "user";

export type GovernedPersonalizationDefaults = {
  schemaVersion: string;
  scope: PersonalizationScope;
  hotkeys: Record<string, string>;
  layoutId: string;
  lockedByAdmin: boolean;
};

export type ClinicianPersonalization = {
  userId: string;
  effective: GovernedPersonalizationDefaults;
  overrides: Partial<GovernedPersonalizationDefaults>;
  changeAudit: Array<{ atIso: string; field: string; from: string; to: string; by: string }>;
};

export function resolveGovernedPersonalization(input: {
  userId: string;
  orgDefaults: GovernedPersonalizationDefaults;
  siteDefaults?: GovernedPersonalizationDefaults | null;
  roleDefaults?: GovernedPersonalizationDefaults | null;
  userOverrides?: Partial<GovernedPersonalizationDefaults> | null;
}): ClinicianPersonalization {
  const layers = [
    input.orgDefaults,
    input.siteDefaults,
    input.roleDefaults,
  ].filter(Boolean) as GovernedPersonalizationDefaults[];

  let effective: GovernedPersonalizationDefaults = {
    ...input.orgDefaults,
    schemaVersion: PERSONALIZATION_SCHEMA_VERSION,
  };
  for (const layer of layers.slice(1)) {
    if (effective.lockedByAdmin) break;
    effective = {
      ...effective,
      ...layer,
      hotkeys: { ...effective.hotkeys, ...layer.hotkeys },
      lockedByAdmin: effective.lockedByAdmin || layer.lockedByAdmin,
    };
  }

  const overrides = input.userOverrides || {};
  if (!effective.lockedByAdmin && Object.keys(overrides).length > 0) {
    effective = {
      ...effective,
      ...overrides,
      hotkeys: { ...effective.hotkeys, ...(overrides.hotkeys || {}) },
      scope: "user",
    };
  }

  return {
    userId: input.userId,
    effective,
    overrides,
    changeAudit: [],
  };
}

export function resetPersonalizationToStandard(
  current: ClinicianPersonalization,
  orgDefaults: GovernedPersonalizationDefaults,
  by: string,
): ClinicianPersonalization {
  return {
    userId: current.userId,
    effective: { ...orgDefaults, schemaVersion: PERSONALIZATION_SCHEMA_VERSION },
    overrides: {},
    changeAudit: [
      ...current.changeAudit,
      {
        atIso: new Date().toISOString(),
        field: "reset",
        from: current.effective.layoutId,
        to: orgDefaults.layoutId,
        by,
      },
    ],
  };
}

export type ReviewSessionEvidenceBundle = {
  schemaVersion: string;
  sessionId: string;
  studyId: string;
  reviewerId: string;
  startedAtIso: string;
  endedAtIso: string;
  metadata: Record<string, string | number | boolean | null>;
  viewedRanges: Array<{ startSeconds: number; endSeconds: number }>;
  qeegPanels: string[];
  annotationIds: string[];
  abnormalSampleIds: string[];
  detectorCandidateIds: string[];
  reportEditIds: string[];
  auditEventIds: string[];
};

export function buildReviewSessionEvidenceBundle(input: {
  sessionId: string;
  studyId: string;
  reviewerId: string;
  startedAt: Date;
  endedAt?: Date;
  metadata?: Record<string, string | number | boolean | null>;
  viewedRanges?: Array<{ startSeconds: number; endSeconds: number }>;
  qeegPanels?: string[];
  annotationIds?: string[];
  abnormalSampleIds?: string[];
  detectorCandidateIds?: string[];
  reportEditIds?: string[];
  auditEventIds?: string[];
}): ReviewSessionEvidenceBundle {
  return {
    schemaVersion: EVIDENCE_BUNDLE_SCHEMA_VERSION,
    sessionId: input.sessionId,
    studyId: input.studyId,
    reviewerId: input.reviewerId,
    startedAtIso: input.startedAt.toISOString(),
    endedAtIso: (input.endedAt || new Date()).toISOString(),
    metadata: input.metadata || {},
    viewedRanges: input.viewedRanges || [],
    qeegPanels: input.qeegPanels || [],
    annotationIds: input.annotationIds || [],
    abnormalSampleIds: input.abnormalSampleIds || [],
    detectorCandidateIds: input.detectorCandidateIds || [],
    reportEditIds: input.reportEditIds || [],
    auditEventIds: input.auditEventIds || [],
  };
}

export function isEvidenceBundleAuditReady(
  bundle: ReviewSessionEvidenceBundle,
): boolean {
  return Boolean(
    bundle.sessionId &&
      bundle.studyId &&
      bundle.reviewerId &&
      bundle.startedAtIso &&
      bundle.endedAtIso &&
      (bundle.viewedRanges.length > 0 ||
        bundle.annotationIds.length > 0 ||
        bundle.auditEventIds.length > 0),
  );
}
